import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, ListChecks, Mic, MessagesSquare, ChevronDown, ChevronUp, Pencil, Trash2, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { relTime } from "@/lib/constants";
import MessageReactions from "@/components/MessageReactions";
import MessageAttachments from "@/components/MessageAttachments";
import MessageComposer from "@/components/MessageComposer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function AiSummaryCard({ msg }) {
  const meta = msg.meta || {};
  return (
    <div className="mt-1 rounded-xl border border-red-900/40 bg-gradient-to-br from-[#1E1013] to-[#160b0d] p-4 max-w-2xl">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-md ruby-gradient flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-white" />
        </span>
        <p className="font-display font-bold text-sm text-red-300">{meta.title || "Meeting Notes"}</p>
      </div>
      {msg.content && (
        <div className="mb-3">
          <MessageText text={msg.content} />
        </div>
      )}
      {meta.notes?.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Key notes</p>
          <ul className="space-y-1">
            {meta.notes.map((n, i) => (
              <li key={i} className="text-sm text-gray-300 flex gap-2">
                <span className="text-red-500 mt-0.5">•</span>
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {meta.todos?.length > 0 && (
        <div className="rounded-lg bg-black/30 border border-[#2E3242] p-3">
          <p className="text-[11px] uppercase tracking-wider text-emerald-400 mb-1.5 flex items-center gap-1.5">
            <ListChecks className="w-3.5 h-3.5" /> {meta.todos.length} tasks posted to #todo-list
          </p>
          <ul className="space-y-1">
            {meta.todos.map((t, i) => (
              <li key={i} className="text-sm text-gray-300 flex gap-2">
                <span className="text-emerald-500">☐</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const LONG_MESSAGE = 320;

function MessageText({ text }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = typeof text === "string" && text.length > LONG_MESSAGE;
  const shown = !isLong || expanded ? text : `${text.slice(0, LONG_MESSAGE).trimEnd()}…`;
  return (
    <div>
      <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap break-words">{shown}</p>
      {isLong && (
        <button
          data-testid="message-expand-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-red-300 hover:text-red-200 transition-colors"
        >
          {expanded ? (<><ChevronUp className="w-3.5 h-3.5" /> Show less</>) : (<><ChevronDown className="w-3.5 h-3.5" /> Read more</>)}
        </button>
      )}
    </div>
  );
}

export default function ChatFeed({ channel, messages, onSend, onOpenVoice, onReact, onOpenThread, onEditMessage, onDeleteMessage }) {
  const { user } = useAuth();
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const isVoiceChannel = channel?.id === "voice-meets-ai";

  const beginEdit = (m) => {
    setEditingId(m.id);
    setEditText(m.content || "");
  };
  const saveEdit = (m) => {
    const t = editText.trim();
    if (t && t !== m.content) onEditMessage?.(m.id, t);
    setEditingId(null);
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  useEffect(() => {
    if (atBottomRef.current) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    atBottomRef.current = true;
  }, [channel?.id]);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#16181F]">
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 md:px-8 py-6" data-testid="chat-feed">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-14 h-14 rounded-2xl ruby-gradient flex items-center justify-center mb-4 ember-glow">
              {isVoiceChannel ? <Mic className="w-6 h-6 text-white" /> : <Send className="w-6 h-6 text-white" />}
            </div>
            <p className="font-display font-bold text-lg text-gray-200">Welcome to #{channel?.name}</p>
            <p className="text-sm text-gray-500 mt-1 max-w-sm">{channel?.description}</p>
            {isVoiceChannel && (
              <Button onClick={onOpenVoice} data-testid="empty-voice-button" className="mt-5 ruby-gradient text-white border border-red-900/40">
                <Mic className="w-4 h-4 mr-1.5" /> Open the AI Assistant
              </Button>
            )}
          </div>
        )}

        <div className="space-y-5 max-w-4xl">
          {messages.map((m) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className="flex gap-3 group" data-testid={`message-${m.id}`}>
              <img src={m.author_avatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-sm text-gray-100">{m.author_name}</span>
                  {m.type === "ai_summary" && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-950/60 text-red-300 border border-red-900/50">AI</span>
                  )}
                  <span className="text-[11px] text-gray-600">{relTime(m.created_at)}</span>
                  {m.edited && <span className="text-[10px] text-gray-600">(edited)</span>}
                  <div className="ml-auto flex items-center gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button data-testid={`reply-thread-${m.id}`} onClick={() => onOpenThread?.(m.id)}
                      className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-red-300">
                      <MessagesSquare className="w-3.5 h-3.5" /> Reply
                    </button>
                    {m.author_id === user?.id && m.type !== "ai_summary" && (
                      <>
                        <button data-testid={`edit-message-${m.id}`} onClick={() => beginEdit(m)}
                          className="text-gray-500 hover:text-blue-300" title="Edit">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`delete-message-${m.id}`} onClick={() => onDeleteMessage?.(m.id)}
                          className="text-gray-500 hover:text-red-400" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editingId === m.id ? (
                  <div className="mt-1">
                    <Textarea
                      data-testid={`edit-input-${m.id}`}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 text-sm min-h-[60px]"
                      autoFocus
                    />
                    <div className="flex items-center gap-2 mt-1.5">
                      <Button data-testid={`edit-save-${m.id}`} onClick={() => saveEdit(m)}
                        className="h-7 px-2.5 text-xs ruby-gradient text-white border border-red-900/40">
                        <Check className="w-3.5 h-3.5 mr-1" /> Save
                      </Button>
                      <button data-testid={`edit-cancel-${m.id}`} onClick={() => setEditingId(null)}
                        className="h-7 px-2 text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1">
                        <X className="w-3.5 h-3.5" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : m.type === "ai_summary" ? (
                  <AiSummaryCard msg={m} />
                ) : (
                  <>
                    {m.content && <MessageText text={m.content} />}
                    <MessageAttachments attachments={m.attachments} />
                  </>
                )}

                {m.reply_count > 0 && (
                  <button data-testid={`thread-count-${m.id}`} onClick={() => onOpenThread?.(m.id)}
                    className="mt-1.5 flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200 bg-red-950/30 border border-red-900/40 rounded-full px-2.5 py-1 transition-colors">
                    <MessagesSquare className="w-3.5 h-3.5" />
                    {m.reply_count} {m.reply_count === 1 ? "reply" : "replies"}
                  </button>
                )}

                <MessageReactions message={m} userId={user?.id} onReact={onReact} />
              </div>
            </motion.div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      <MessageComposer placeholder={`Message #${channel?.name}`} onSend={onSend} />
    </div>
  );
}
